import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { UsersById } from 'interfaces/userCredentials';
import React from 'react';
import useUsersById from 'utils/queryHooks/useUsersById';

export interface UserIdentityProps {
  userId?: string | null;
  displayName?: string | null;
  fallbackLabel?: string | null;
  usersById?: UsersById;
}

export const getUserContextDetails = (usersById?: UsersById, userId?: string | null) => {
  const entry = userId ? usersById?.[userId] : undefined;
  return entry && (entry.name || entry.primaryEmail)
    ? { id: entry.id, name: entry.name || undefined, primaryEmail: entry.primaryEmail || undefined }
    : undefined;
};

export const UserIdentity = ({ userId, displayName, fallbackLabel, usersById }: UserIdentityProps) => {
  const hasResponseContext = Boolean(usersById);
  const directory = useUsersById({ enabled: Boolean(userId) && !hasResponseContext });
  const user = hasResponseContext ? getUserContextDetails(usersById, userId) : directory.getUserDetailsById(userId);
  const canLoadUserDetails = !hasResponseContext && directory.canLoadUserDetails;
  const isUsersError = !hasResponseContext && directory.isUsersError;
  const loading = Boolean(userId) && canLoadUserDetails && directory.isLoadingUsers;
  const label =
    displayName?.trim() ||
    user?.name?.trim() ||
    user?.primaryEmail?.trim() ||
    fallbackLabel?.trim() ||
    (!userId ? 'Not recorded' : loading ? 'Loading user...' : 'User details unavailable');

  if (!userId) return <span>{label}</span>;

  return (
    <Tooltip
      describeChild
      enterDelay={0}
      title={
        <Stack spacing={0.5}>
          <Typography variant="caption">Name: {user?.name || 'Unavailable'}</Typography>
          <Typography variant="caption">Email: {user?.primaryEmail || 'Unavailable'}</Typography>
          <Typography variant="caption">Recorded user ID: {userId}</Typography>
          {user && user.id !== userId && <Typography variant="caption">Internal user ID: {user.id}</Typography>}
          {!user && (
            <Typography variant="caption">
              {hasResponseContext
                ? 'No user details are available for this recorded ID.'
                : loading
                ? 'Loading accessible user details.'
                : isUsersError
                ? 'User details could not be loaded.'
                : !canLoadUserDetails
                ? 'User-directory access is unavailable.'
                : 'User was not found in the accessible directory.'}
            </Typography>
          )}
        </Stack>
      }
    >
      <Box component="span" tabIndex={0} sx={{ cursor: 'help' }}>
        {label}
      </Box>
    </Tooltip>
  );
};
